
import React from 'react';
import { AlertTriangle, Clock, CheckCircle, Wifi, WifiOff } from 'lucide-react';
import { useOfflineStatus } from '@/hooks/useOfflineStatus';
import { useThemeConfig } from '@/components/ui/theme-config-provider';

interface OfflineShoppingListStatusProps {
  className?: string;
}

export const OfflineShoppingListStatus: React.FC<OfflineShoppingListStatusProps> = ({
  className = ""
}) => {
  const {
    isOnline,
    pendingOperations,
    lastSyncAttempt,
    syncErrors,
    hasErrors,
    triggerSync,
    clearSyncErrors
  } = useOfflineStatus();
  const { theme } = useThemeConfig();

  const showTopStatus = !isOnline || pendingOperations > 0;
  const showSyncedMessage = isOnline && pendingOperations === 0 && lastSyncAttempt;

  if (!showTopStatus && !showSyncedMessage && !hasErrors) {
    return null;
  }

  // ✅ FIX: Status visivo migliorato con icone e colori più chiari
  return (
    <div className={`space-y-2 ${className}`}>
      {/* Status principale con indicatore di connessione */}
      {showTopStatus && (
        <div
          className="flex items-center gap-2 p-2 rounded-lg border"
          style={{ backgroundColor: theme.cardBackgroundColor, borderColor: theme.borderColor, color: isOnline ? theme.successColor : theme.warningColor }}
        >
          {isOnline ? (
            <Wifi size={16} />
          ) : (
            <WifiOff size={16} />
          )}
          <span className="text-sm font-medium">
            {isOnline ? 'Online' : 'Offline'}
          </span>
          {pendingOperations > 0 && (
            <span
              className="text-xs px-2 py-1 rounded"
              style={{ backgroundColor: `${theme.primaryColor}20`, color: theme.primaryColor }}
            >
              {pendingOperations} in coda
            </span>
          )}
        </div>
      )}

      
      {hasErrors && (
        <div
          className="flex items-center gap-2 p-2 rounded border"
          style={{ backgroundColor: `${theme.errorColor}20`, borderColor: theme.errorColor, color: theme.errorColor }}
        >
          <AlertTriangle size={16} />
          <div className="flex-1">
            <p className="text-sm font-medium">Errori di sincronizzazione</p>
            <ul className="text-xs mt-1">
              {syncErrors.slice(0, 3).map((error, index) => (
                <li key={index}>• {error}</li>
              ))}
            </ul>
          </div>
          <button
            onClick={clearSyncErrors}
            className="text-xs underline"
            style={{ color: theme.errorColor }}
          >
            Nascondi
          </button>
        </div>
      )}

      {!isOnline && pendingOperations > 0 && (
        <div
          className="flex items-center gap-2 p-2 rounded border"
          style={{ backgroundColor: `${theme.warningColor}20`, borderColor: theme.warningColor, color: theme.warningColor }}
        >
          <Clock size={16} />
          <div className="flex-1">
            <p className="text-sm font-medium">Modifiche in attesa</p>
            <p className="text-xs">
              {pendingOperations} operazioni verranno sincronizzate al ritorno online
            </p>
          </div>
        </div>
      )}

      {showSyncedMessage && (
        <div
          className="flex items-center gap-2 p-2 rounded border"
          style={{ backgroundColor: `${theme.successColor}20`, borderColor: theme.successColor, color: theme.successColor }}
        >
          <CheckCircle size={16} />
          <div className="flex-1">
            <p className="text-sm font-medium">Sincronizzato</p>
            <p className="text-xs">
              Ultima sincronizzazione: {lastSyncAttempt.toLocaleTimeString()}
            </p>
          </div>
        </div>
      )}

      {isOnline && pendingOperations > 0 && (
        <div
          className="flex items-center gap-2 p-2 rounded border"
          style={{ backgroundColor: `${theme.primaryColor}20`, borderColor: theme.primaryColor, color: theme.primaryColor }}
        >
          <Clock size={16} />
          <div className="flex-1">
            <p className="text-sm font-medium">Sincronizzazione in corso</p>
            <p className="text-xs">
              {pendingOperations} operazioni in coda
            </p>
          </div>
          <button
            onClick={triggerSync}
            className="text-xs underline hover:text-[var(--link-hover)]"
            style={{
              color: theme.primaryColor,
              '--link-hover': theme.textPrimaryColor,
            } as React.CSSProperties}
          >
            Forza sync
          </button>
        </div>
      )}
    </div>
  );
};

export default OfflineShoppingListStatus;
